import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown } from "lucide-react";
import CustomDropdown from "../../../../../components/CustomDropdown/CustomDropdown";

type Frequency = "day" | "week" | "month" | "year";
type Format = "excel" | "pdf" | "csv";

interface Props {
  open: boolean;
  onClose: () => void;
  accountName: string;
}

export default function ScheduleReportModal({ open, onClose, accountName }: Props) {
  const [name, setName] = useState("Schedule and send a report");
  const [action, setAction] = useState("schedule-send");
  const [startDate, setStartDate] = useState(todayDMY());
  const [startTime, setStartTime] = useState("9:00 AM");
  const [repeatEvery, setRepeatEvery] = useState("1");
  const [repeatUnit, setRepeatUnit] = useState<Frequency>("day");
  const [endMode, setEndMode] = useState<"date" | "never">("date");
  const [endDate, setEndDate] = useState("31/12/2026");
  const [sendEnabled, setSendEnabled] = useState(true);
  const [sendTo, setSendTo] = useState("");
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("Account quick report");
  const [format, setFormat] = useState<Format>("excel");
  const [message, setMessage] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { if (open) setDirty(false); }, [open]);

  if (!open) return null;

  const markDirty = () => setDirty(true);

  const save = (active: boolean) => {
    try {
      const existing = window.localStorage.getItem("novala.scheduledReports");
      const list = existing ? JSON.parse(existing) : [];
      list.push({
        id: `sr-${Date.now()}`,
        name: name.trim() || "Schedule and send a report",
        accountName,
        action,
        reportName: "Account quick report",
        schedule: { startDate, startTime, repeatEvery: parseInt(repeatEvery, 10) || 1, repeatUnit, endMode, endDate: endMode === "date" ? endDate : null },
        send: sendEnabled ? { to: sendTo, cc, bcc, subject, format, message } : null,
        active,
        createdAt: new Date().toISOString(),
      });
      window.localStorage.setItem("novala.scheduledReports", JSON.stringify(list));
    } catch {}
    onClose();
  };

  const handleCancel = () => { if (dirty) setDiscardOpen(true); else onClose(); };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={handleCancel} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h2 className="text-lg font-bold text-ink">Create workflow</h2>
          <button type="button" onClick={handleCancel} aria-label="Close" className="text-ink3 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-[13px] text-ink mb-2 font-medium">Workflow name</label>
              <input value={name} onChange={(e) => { setName(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white focus:outline-none focus:border-teal-dark focus:ring-2 focus:ring-teal-dark/10" />
            </div>
            <div>
              <label className="block text-[13px] text-ink mb-2 font-medium">Source transaction</label>
              <LockedTrigger label="Report" />
            </div>
          </div>

          <div className="pt-3 pb-3 border-t border-[#EEF1F4]">
            <div className="text-[13px] font-bold text-ink mb-3">1. Do this in Novala</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[13px] text-ink mb-2 font-medium">What do you want Novala to do?</label>
                <CustomDropdown
                  value={action}
                  onChange={(v) => { setAction(v); markDirty(); }}
                  options={[
                    { label: "Schedule and send", value: "schedule-send" },
                    { label: "Send only", value: "send-only" },
                    { label: "Save a snapshot", value: "snapshot" },
                  ]}
                />
              </div>
              <div>
                <label className="block text-[13px] text-ink mb-2 font-medium">Report name</label>
                <LockedTrigger label="Account quick report" />
              </div>
            </div>
            <p className="text-[12px] text-ink3 mb-3"><strong>Note:</strong> We'll automatically send reports on the selected schedule.</p>
            <div className="bg-[#F7F9FB] p-3 rounded-lg">
              <div className="text-[12px] font-bold mb-3">Create and send recurring report</div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-[13px] text-ink mb-2 font-medium">Start</label>
                  <input value={startDate} onChange={(e) => { setStartDate(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                </div>
                <div>
                  <label className="block text-[13px] text-ink mb-2 font-medium">At</label>
                  <input value={startTime} onChange={(e) => { setStartTime(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                </div>
                <div>
                  <label className="block text-[13px] text-ink mb-2 font-medium">Repeat every</label>
                  <div className="flex gap-2">
                    <input value={repeatEvery} onChange={(e) => { setRepeatEvery(e.target.value); markDirty(); }} className="w-16 h-[42px] border border-[#D8DEE6] rounded-lg px-2 text-[13.5px] bg-white text-center" />
                    <div className="flex-1">
                      <CustomDropdown
                        value={repeatUnit}
                        onChange={(v) => { setRepeatUnit(v as Frequency); markDirty(); }}
                        options={[
                          { label: "Day", value: "day" },
                          { label: "Week", value: "week" },
                          { label: "Month", value: "month" },
                          { label: "Year", value: "year" },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-5 items-center mt-3 text-[13px] text-ink2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="end" checked={endMode === "date"} onChange={() => { setEndMode("date"); markDirty(); }} className="accent-teal-dark w-4 h-4" />
                  End:
                  <input value={endDate} onChange={(e) => { setEndDate(e.target.value); markDirty(); }} className="w-36 h-9 border border-[#D8DEE6] rounded-md px-2 text-[13px] bg-white ml-1" />
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="end" checked={endMode === "never"} onChange={() => { setEndMode("never"); markDirty(); }} className="accent-teal-dark w-4 h-4" /> Never
                </label>
              </div>
            </div>
          </div>

          <div className="pt-3 pb-3 border-t border-[#EEF1F4]">
            <div className="text-[13px] font-bold text-ink mb-3">2. How would you like this action to happen?</div>
            <div className="border border-[#D8DEE6] rounded-lg p-4">
              <label className="inline-flex items-center gap-2 text-[13.5px] font-bold mb-3 cursor-pointer">
                <input type="checkbox" checked={sendEnabled} onChange={(e) => { setSendEnabled(e.target.checked); markDirty(); }} className="accent-teal-dark w-4 h-4" /> Send a company email
              </label>
              {sendEnabled && (
                <>
                  <div className="mb-3">
                    <label className="block text-[13px] text-ink mb-2 font-medium">Send to</label>
                    <input value={sendTo} onChange={(e) => { setSendTo(e.target.value); markDirty(); }} placeholder="Separate multiple emails with commas" className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                    <div className="mt-1.5 text-[12px]">
                      <button type="button" onClick={() => setCcVisible(true)} className="text-teal-dark font-semibold">Add CC</button>
                      <span className="text-ink3 mx-2">|</span>
                      <button type="button" onClick={() => setBccVisible(true)} className="text-teal-dark font-semibold">Add BCC</button>
                    </div>
                  </div>
                  {ccVisible && (
                    <div className="mb-3">
                      <label className="block text-[13px] text-ink mb-2 font-medium">CC</label>
                      <input value={cc} onChange={(e) => { setCc(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                    </div>
                  )}
                  {bccVisible && (
                    <div className="mb-3">
                      <label className="block text-[13px] text-ink mb-2 font-medium">BCC</label>
                      <input value={bcc} onChange={(e) => { setBcc(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="block text-[13px] text-ink mb-2 font-medium">Subject</label>
                    <input value={subject} onChange={(e) => { setSubject(e.target.value); markDirty(); }} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-[13px] text-ink mb-2 font-medium">Format</label>
                    <div className="flex gap-2">
                      {(["excel","pdf","csv"] as Format[]).map((f) => {
                        const selected = format === f;
                        const label = f === "excel" ? "Excel" : f === "pdf" ? "PDF" : "CSV";
                        return (
                          <button key={f} type="button" onClick={() => { setFormat(f); markDirty(); }} className={`h-[42px] px-5 rounded-lg text-[13.5px] font-semibold ${selected ? "border-2 border-teal-dark bg-[#F0F6FC] text-ink" : "border border-[#D8DEE6] bg-white text-ink hover:bg-[#EEF4FB]"}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] text-ink mb-2 font-medium">Message</label>
                    <textarea value={message} onChange={(e) => { setMessage(e.target.value); markDirty(); }} placeholder="Hello, please find the attached Account quick report." className="w-full min-h-[90px] border border-[#D8DEE6] rounded-lg px-3.5 py-2.5 text-[13.5px] font-sans resize-y bg-white" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center px-5 py-3 border-t border-[#EEF1F4] bg-[#F7F9FB]">
          <button type="button" onClick={handleCancel} className="text-[13.5px] text-ink2 font-semibold">Cancel</button>
          <div className="flex gap-2.5">
            <button type="button" onClick={() => save(false)} className="h-[40px] px-5 border border-[#D8DEE6] rounded-[10px] text-[13.5px] font-semibold text-ink bg-white">Save</button>
            <button type="button" onClick={() => save(true)} className="h-[40px] px-5 border border-ink rounded-[10px] text-[13.5px] font-semibold text-white bg-ink">Save and turn on</button>
          </div>
        </div>
      </div>

      {discardOpen && (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDiscardOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-5 text-center">
              <div className="w-12 h-12 rounded-full bg-[#FEF3C7] text-[#D97706] inline-flex items-center justify-center text-xl font-bold mx-auto mb-3">!</div>
              <h3 className="text-base font-bold text-ink mb-1.5">Discard Changes</h3>
              <p className="text-[12.5px] text-ink3">We noticed you've made some changes you haven't saved. Would you like to discard the changes and continue?</p>
            </div>
            <div className="flex justify-center gap-2.5 px-5 py-3 border-t border-[#EEF1F4] bg-[#F7F9FB]">
              <button type="button" onClick={() => setDiscardOpen(false)} className="h-[40px] px-5 border border-[#D8DEE6] rounded-[10px] text-[13.5px] font-semibold text-ink bg-white">Cancel</button>
              <button type="button" onClick={() => { setDiscardOpen(false); onClose(); }} className="h-[40px] px-5 border border-ink rounded-[10px] text-[13.5px] font-semibold text-white bg-ink">Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

function LockedTrigger({ label }: { label: string }) {
  return (
    <div className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-[#F7F9FB] text-ink3 flex items-center justify-between cursor-not-allowed">
      <span>{label}</span>
      <ChevronDown className="w-4 h-4 text-ink3" />
    </div>
  );
}

function todayDMY(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}